/**
 * SupabaseBookmarkOrchestrator
 *
 * Coordinates between SupabaseBookmarkAgent, SupabaseBookmarkValidator,
 * and localStorage fallback for unified bookmark operations.
 *
 * Implements singleton pattern for global state management.
 * Provides pub/sub mechanism for React component integration.
 *
 * Features:
 * - Unified API for bookmark operations
 * - Automatic localStorage fallback when not authenticated
 * - Migration from localStorage to database on sign-in
 * - State change notifications for React
 */

import SupabaseBookmarkAgent from './agents/SupabaseBookmarkAgent';
import SupabaseBookmarkValidator from './validators/SupabaseBookmarkValidator';

const LOCAL_STORAGE_KEY = 'flashcard_bookmarks_v2';
const MIGRATION_FLAG_KEY = 'bookmarks_migrated_to_db';

class SupabaseBookmarkOrchestrator {
  constructor() {
    // Singleton enforcement
    if (SupabaseBookmarkOrchestrator._instance) {
      return SupabaseBookmarkOrchestrator._instance;
    }
    SupabaseBookmarkOrchestrator._instance = this;

    // Internal state
    this._isInitialized = false;
    this._isAuthenticated = false;
    this._userId = null;
    this._subscribers = new Set();
    this._lastError = null;
    this._localBookmarks = new Set();

    // Agent references
    this._bookmarkAgent = SupabaseBookmarkAgent;
    this._validator = SupabaseBookmarkValidator;
  }

  /**
   * Initialize the orchestrator
   * @param {boolean} isAuthenticated - Whether user is authenticated
   * @param {string|null} userId - The Auth0 user ID (user.sub)
   * @returns {Promise<{ success: boolean, count: number, error?: string }>}
   */
  async initialize(isAuthenticated, userId) {
    try {
      this._isAuthenticated = isAuthenticated;
      this._userId = userId;

      if (isAuthenticated && userId) {
        // Set up agent with user ID
        this._bookmarkAgent.setAuth0Id(userId);

        // Load bookmarks from database
        const result = await this._bookmarkAgent.execute('GET_BOOKMARKS');

        // Check for pending migration
        await this._migratLocalBookmarksIfNeeded();

        this._isInitialized = true;
        this._notifySubscribers();

        return {
          success: true,
          count: result.count || 0
        };
      } else {
        // Load from localStorage
        this._loadLocalBookmarks();
        this._isInitialized = true;
        this._notifySubscribers();

        return {
          success: true,
          count: this._localBookmarks.size
        };
      }
    } catch (error) {
      console.error('[SupabaseBookmarkOrchestrator] Initialize error:', error);
      this._lastError = error.message;
      this._isInitialized = true;

      // Fallback to localStorage
      this._loadLocalBookmarks();
      this._notifySubscribers();

      return {
        success: false,
        count: this._localBookmarks.size,
        error: error.message
      };
    }
  }

  /**
   * Switch storage mode (called on login/logout)
   * @param {boolean} isAuthenticated - Whether user is authenticated
   * @param {string|null} userId - The Auth0 user ID
   */
  async switchStorageMode(isAuthenticated, userId) {
    this._isAuthenticated = isAuthenticated;
    this._userId = userId;

    if (isAuthenticated && userId) {
      this._bookmarkAgent.setAuth0Id(userId);
      await this._bookmarkAgent.execute('GET_BOOKMARKS');
      await this._migratLocalBookmarksIfNeeded();
    } else {
      this._bookmarkAgent.setAuth0Id(null);
      this._loadLocalBookmarks();
    }

    this._notifySubscribers();
  }

  /**
   * Toggle bookmark state for a flashcard
   * @param {string|number} flashcardId - The flashcard ID to toggle
   * @returns {Promise<{ success: boolean, isBookmarked: boolean, count: number, error?: string }>}
   */
  async toggleBookmark(flashcardId) {
    // Convert ID to string for consistent handling
    const id = flashcardId != null ? String(flashcardId) : null;

    // Validate flashcard ID
    const validation = this._validator.execute('VALIDATE_FLASHCARD_ID', id);
    if (!validation.valid) {
      return {
        success: false,
        isBookmarked: this.isBookmarked(id),
        count: this.getBookmarkCount(),
        error: validation.error
      };
    }

    if (this._isAuthenticated && this._userId) {
      // Use database
      const result = await this._bookmarkAgent.execute('TOGGLE_BOOKMARK', id);
      this._notifySubscribers();
      return result;
    } else {
      // Use localStorage
      const wasBookmarked = this._localBookmarks.has(id);

      if (wasBookmarked) {
        this._localBookmarks.delete(id);
      } else {
        this._localBookmarks.add(id);
      }

      this._saveLocalBookmarks();
      this._notifySubscribers();

      return {
        success: true,
        isBookmarked: !wasBookmarked,
        count: this._localBookmarks.size
      };
    }
  }

  /**
   * Check if a flashcard is bookmarked
   * @param {string|number} flashcardId - The flashcard ID to check
   * @returns {boolean}
   */
  isBookmarked(flashcardId) {
    if (flashcardId == null) return false;

    // Convert ID to string for consistent comparison
    const id = String(flashcardId);

    if (this._isAuthenticated && this._userId) {
      const result = this._bookmarkAgent.execute('IS_BOOKMARKED', id);
      return result.isBookmarked;
    } else {
      return this._localBookmarks.has(id);
    }
  }

  /**
   * Get all bookmarked flashcard IDs
   * @returns {string[]}
   */
  getBookmarks() {
    if (this._isAuthenticated && this._userId) {
      const state = this._bookmarkAgent.execute('GET_STATE');
      return state.bookmarkedIds;
    } else {
      return Array.from(this._localBookmarks);
    }
  }

  /**
   * Get bookmarked flashcards from a list
   * @param {Array} allFlashcards - Array of all flashcards
   * @returns {Array}
   */
  getBookmarkedFlashcards(allFlashcards) {
    if (!Array.isArray(allFlashcards)) return [];

    const bookmarkedIds = this.getBookmarks();
    const bookmarkedSet = new Set(bookmarkedIds);

    return allFlashcards.filter(card => {
      const cardId = card.id || card.flashcardId || card._id;
      return bookmarkedSet.has(cardId);
    });
  }

  /**
   * Get count of bookmarked flashcards
   * @returns {number}
   */
  getBookmarkCount() {
    if (this._isAuthenticated && this._userId) {
      const state = this._bookmarkAgent.execute('GET_STATE');
      return state.count;
    } else {
      return this._localBookmarks.size;
    }
  }

  /**
   * Clear all bookmarks
   * @returns {Promise<{ success: boolean, clearedCount: number, error?: string }>}
   */
  async clearAllBookmarks() {
    try {
      if (this._isAuthenticated && this._userId) {
        const result = await this._bookmarkAgent.execute('CLEAR_ALL_BOOKMARKS');
        this._notifySubscribers();
        return result;
      } else {
        const clearedCount = this._localBookmarks.size;
        this._localBookmarks.clear();
        this._saveLocalBookmarks();
        this._notifySubscribers();

        return {
          success: true,
          clearedCount
        };
      }
    } catch (error) {
      return {
        success: false,
        clearedCount: 0,
        error: error.message
      };
    }
  }

  /**
   * Subscribe to state changes
   * @param {Function} callback - Function to call on state change
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this._subscribers.add(callback);

    return () => {
      this._subscribers.delete(callback);
    };
  }

  /**
   * Get current state
   * @returns {{ isInitialized: boolean, isAuthenticated: boolean, bookmarkedIds: string[], count: number, isLoading: boolean, lastError: string|null }}
   */
  getState() {
    const bookmarkedIds = this.getBookmarks();
    const isLoading = this._isAuthenticated && this._userId
      ? this._bookmarkAgent.execute('GET_STATE').isLoading
      : false;

    return {
      isInitialized: this._isInitialized,
      isAuthenticated: this._isAuthenticated,
      bookmarkedIds,
      count: bookmarkedIds.length,
      isLoading,
      lastError: this._lastError
    };
  }

  /**
   * Load bookmarks from localStorage
   * @private
   */
  _loadLocalBookmarks() {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert all IDs to strings for consistent comparison
        const ids = Array.isArray(parsed) ? parsed.map(id => String(id)) : [];
        this._localBookmarks = new Set(ids);
      } else {
        this._localBookmarks = new Set();
      }
    } catch (e) {
      console.warn('[SupabaseBookmarkOrchestrator] Failed to load local bookmarks:', e);
      this._localBookmarks = new Set();
    }
  }

  /**
   * Save bookmarks to localStorage
   * @private
   */
  _saveLocalBookmarks() {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.from(this._localBookmarks)));
    } catch (e) {
      console.warn('[SupabaseBookmarkOrchestrator] Failed to save local bookmarks:', e);
    }
  }

  /**
   * Migrate local bookmarks to database if needed
   * @private
   */
  async _migratLocalBookmarksIfNeeded() {
    try {
      // Check if already migrated
      const migrationKey = `${MIGRATION_FLAG_KEY}_${this._userId}`;
      const alreadyMigrated = localStorage.getItem(migrationKey);

      if (alreadyMigrated) {
        return;
      }

      // Load local bookmarks
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!stored) {
        localStorage.setItem(migrationKey, 'true');
        return;
      }

      const localBookmarks = JSON.parse(stored);
      if (!Array.isArray(localBookmarks) || localBookmarks.length === 0) {
        localStorage.setItem(migrationKey, 'true');
        return;
      }

      // Sync to database
      console.log(`[SupabaseBookmarkOrchestrator] Migrating ${localBookmarks.length} bookmarks to database`);
      const result = await this._bookmarkAgent.execute('SYNC_BOOKMARKS', localBookmarks);

      if (result.success) {
        localStorage.setItem(migrationKey, 'true');
        console.log(`[SupabaseBookmarkOrchestrator] Migration complete: ${result.syncedCount} bookmarks synced`);
      }
    } catch (error) {
      console.error('[SupabaseBookmarkOrchestrator] Migration error:', error);
    }
  }

  /**
   * Notify all subscribers of state change
   * @private
   */
  _notifySubscribers() {
    const state = this.getState();
    this._subscribers.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('[SupabaseBookmarkOrchestrator] Subscriber error:', error);
      }
    });
  }

  /**
   * Get singleton instance
   * @static
   * @returns {SupabaseBookmarkOrchestrator}
   */
  static getInstance() {
    if (!SupabaseBookmarkOrchestrator._instance) {
      new SupabaseBookmarkOrchestrator();
    }
    return SupabaseBookmarkOrchestrator._instance;
  }

  /**
   * Reset singleton (for testing)
   * @static
   */
  static resetInstance() {
    if (SupabaseBookmarkOrchestrator._instance) {
      SupabaseBookmarkOrchestrator._instance._subscribers.clear();
      SupabaseBookmarkOrchestrator._instance = null;
    }
  }
}

// Singleton instance
SupabaseBookmarkOrchestrator._instance = null;

export default SupabaseBookmarkOrchestrator;
export { SupabaseBookmarkOrchestrator };
